/**
 * useAnnotativeObjectScaleDialog — Track 3 Phase 4
 *
 * Owns the per-entity Annotative Object Scale dialog feature (PR-9)
 * extracted from `App.tsx` as part of the Track 3 App.tsx decomposition
 * (see `docs/plans/2026-05-08_track3_app_decomposition.md`).
 *
 * Responsibilities:
 *   - Owns the `annotativeObjectScaleDialogEntity` state (target entity
 *     id, or `null` when closed).
 *   - Exposes imperative `openForEntity(entityId)` / `close()` — the
 *     dialog is opened from the Properties palette "Annotative scales"
 *     "..." button (target entity captured at the time of click).
 *   - Resolves the target entity across `spaces` and `blocks` so the
 *     dialog can read the current `annotativeContexts[]` payload.
 *   - Owns Add / Remove handlers — Add builds a minimal context derived
 *     from the catalogue (per-entity-kind defaults filled in) and
 *     dispatches `entity/add-annotation-context`; Remove dispatches
 *     `entity/remove-annotation-context`.
 *   - When the resolved entity vanishes (defensive), the hook returns
 *     `props === null` so the orchestrator skips the mount.
 *
 * Returned shape:
 *   {
 *     isOpen,
 *     entityId,
 *     props: { open, entityId, entityType, attachedScales,
 *              catalogueScales, onAdd, onRemove, onClose } | null,
 *     openForEntity,
 *     close,
 *   }
 *
 * AutoCAD parity:
 *   The Annotative Object Scale dialog (Properties palette) lets users
 *   attach/detach individual annotation scales to a single annotative
 *   object. The dialog component preserves the AutoCAD-parity surface;
 *   the hook only owns orchestration, entity resolution, and the
 *   per-kind context-shape derivation for `entity/add-annotation-context`.
 */

import { useCallback, useMemo, useState } from "react";

import { useEditorStore } from "../state/editorStore";

export interface UseAnnotativeObjectScaleDialogResult {
  /** Whether to mount `<AnnotativeObjectScaleDialog>`. */
  isOpen: boolean;
  /** Target entity id, or `null` when closed. */
  entityId: string | null;
  /**
   * Props bundle for `<AnnotativeObjectScaleDialog>`. `null` when
   * closed OR when the entity could not be resolved (defensive).
   */
  props: {
    open: true;
    entityId: string;
    entityType: string;
    attachedScales: ReadonlyArray<{ scaleName: string; scaleFactor: number }>;
    catalogueScales: ReadonlyArray<{ name: string; scaleFactor: number }>;
    onAdd: (scaleName: string) => void;
    onRemove: (scaleName: string) => void;
    onClose: () => void;
  } | null;
  /**
   * Imperative open — captures the target entity id from the click on
   * the Properties palette's "Annotative scales" "..." button.
   */
  openForEntity: (entityId: string) => void;
  /** Imperative close — used by `resetShellState()` and `onClose`. */
  close: () => void;
}

export function useAnnotativeObjectScaleDialog(): UseAnnotativeObjectScaleDialogResult {
  const dispatch = useEditorStore((store) => store.dispatch);
  const geometryDocument = useEditorStore((store) => store.geometryDocument);

  const [entityId, setEntityId] = useState<string | null>(null);

  const openForEntity = useCallback((id: string) => {
    setEntityId(id);
  }, []);

  const close = useCallback(() => setEntityId(null), []);

  const onAdd = useCallback(
    (scaleName: string) => {
      if (!entityId) return;
      // Resolve the catalogue entry to derive the scale factor.
      const catalogue = geometryDocument.annotationScales?.find(
        (s) => s.name === scaleName,
      );
      if (!catalogue) return;
      // Resolve the target entity (across spaces + block defs) so we
      // can branch on entity type and pick the right context kind.
      const allEntities = [
        ...geometryDocument.spaces.flatMap((s) => s.entities),
        ...Object.values(geometryDocument.blocks ?? {}).flatMap(
          (b) => b.entities,
        ),
      ];
      const entity = allEntities.find((e) => e.id === entityId);
      if (!entity) return;
      const annotEntity = entity as { type: string };
      const baseFields = {
        scaleName,
        scaleFactor: catalogue.scaleFactor,
        flags: 0,
      };
      let context: unknown;
      switch (annotEntity.type) {
        case "Text":
          context = {
            kind: "text",
            ...baseFields,
            insertion: { x: 0, y: 0 },
            rotation: 0,
          };
          break;
        case "MText":
          context = {
            kind: "mtext",
            ...baseFields,
            insertion: { x: 0, y: 0 },
            rotation: 0,
          };
          break;
        case "Hatch":
          context = {
            kind: "hatch",
            ...baseFields,
            patternScale: 1,
            patternAngle: 0,
          };
          break;
        case "Block":
          context = {
            kind: "blockref",
            ...baseFields,
            insertion: { x: 0, y: 0 },
            scaleX: 1,
            scaleY: 1,
            rotation: 0,
          };
          break;
        case "Attdef":
        default:
          context = {
            kind: "attribute",
            ...baseFields,
            insertion: { x: 0, y: 0 },
            rotation: 0,
            attribTag: "",
          };
          break;
      }
      dispatch({
        type: "entity/add-annotation-context",
        source: "scene",
        entityId,
        context,
      });
    },
    [dispatch, entityId, geometryDocument],
  );

  const onRemove = useCallback(
    (scaleName: string) => {
      if (!entityId) return;
      dispatch({
        type: "entity/remove-annotation-context",
        source: "scene",
        entityId,
        scaleName,
      });
    },
    [dispatch, entityId],
  );

  // Resolve target entity for prop-bundle construction. Memoized so a
  // bag of entity scans only happens once per render.
  const resolved = useMemo(() => {
    if (!entityId) return null;
    const allEntities = [
      ...geometryDocument.spaces.flatMap((s) => s.entities),
      ...Object.values(geometryDocument.blocks ?? {}).flatMap(
        (b) => b.entities,
      ),
    ];
    const entity = allEntities.find((e) => e.id === entityId);
    if (!entity) return null;
    const annotEntity = entity as {
      type: string;
      annotativeContexts?: ReadonlyArray<{
        scaleName: string;
        scaleFactor?: number;
        scale?: number;
      }>;
    };
    const attachedScales = (annotEntity.annotativeContexts ?? []).map((c) => ({
      scaleName: c.scaleName,
      scaleFactor: c.scaleFactor ?? c.scale ?? 1,
    }));
    return { entityType: annotEntity.type, attachedScales };
  }, [entityId, geometryDocument]);

  const props =
    entityId && resolved
      ? ({
          open: true as const,
          entityId,
          entityType: resolved.entityType,
          attachedScales: resolved.attachedScales,
          catalogueScales: geometryDocument.annotationScales ?? [],
          onAdd,
          onRemove,
          onClose: close,
        })
      : null;

  return {
    isOpen: entityId !== null,
    entityId,
    props,
    openForEntity,
    close,
  };
}
